/**
 * src/lib/api/projects.js — Project read/write API.
 * Returns raw snake_case Supabase rows. See docs/SHAPE_CONTRACT.md.
 */

import { supabase } from '../supabase';
import { apiFetch, API_BASE } from './client';
import { indexKnowledge } from './knowledge-indexer';
import { BUNDLE_WITH_ITEMS, withBundleItems } from './bundles';
import { logEvent } from '../observability';

const SELECT_PROJECT = '*, last_email_at, emails_seen_at, user_profiles!owner_id(display_name), project_types!project_type_id(id, slug, label_en, label_es), stages!stage_id(id, stage_name, label_en, label_es, color), vessels!vessel_id(id, vessel_name, make_model, year, loa_m, flag_registration, home_port_name, berth_number, slip_number, pier_number, location_map_url, location_note, location_updated_at, location_updated_by), crm_deals!deal_id(id, created_at, won_date)'; // TICKET-209 Phase 2: location columns; TICKET-211: unread email columns; TICKET-696: lead created + confirmation dates

export async function fetchProjects() {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select(SELECT_PROJECT)
      .order('project_number', { ascending: false });
    if (error) { console.warn('[projects] fetch error:', error.message); return []; }
    return (data || []).filter(r => r.project_name && r.project_name.trim() !== '');
  } catch (err) {
    console.warn('[projects] fetchProjects failed:', err.message);
    return [];
  }
}

// Hard ceiling on project INSERT: 15s. Above this the user is looking at a
// dead modal with no feedback; fail fast so the caller can surface a toast.
// Root cause of the 2026-04-24 "stuck in creating + data loss" incident
// (Bug 1 + Bug 2): inserts were silently hanging/rejecting with no visible
// error, so the CEO and PMO both lost ~2 days of project data.
const CREATE_PROJECT_TIMEOUT_MS = 15000;

export async function createProject(payload) {
  try {
    // Pre-flight auth check. If the JWT expired, user_app_role() on the RLS
    // policy returns 'anonymous' and the INSERT silently rejects — but the
    // original code caught nothing, so the user saw no feedback. Surface a
    // clear "session expired" error instead of a generic timeout.
    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user?.id) {
      return { success: false, error: 'Session expired — please refresh the page and sign in again.', code: 'SESSION_EXPIRED' };
    }

    const row = { project_name: payload.project_name || payload.Project_Name };
    // Offline sync: accept a client uuid so offline create+edit share one id and
    // replay is PK-idempotent. Online creates omit id → server default fills it.
    if (payload.id) row.id = payload.id;
    if (payload.short_description) row.short_description = payload.short_description;
    if (payload.owner_id) row.owner_id = payload.owner_id;
    if (payload.project_type_id) row.project_type_id = payload.project_type_id;
    if (payload.stage_id) row.stage_id = payload.stage_id;
    if (payload.vessel_id) row.vessel_id = payload.vessel_id;
    if (payload.deal_id) row.deal_id = payload.deal_id;
    if (payload.flow_id) row.flow_id = payload.flow_id;

    // Auto-populate flow_id + stage_id from project_type.default_flow_id when
    // caller supplied a project_type but left flow/stage blank. Prevents
    // Muluc-class orphans (migration 085 backfilled existing rows; this closes
    // the door so new projects can't land NULL).
    //
    // Atomicity: we only write flow_id if we ALSO resolved stage_id. A half-
    // populated row (flow_id set, stage_id NULL) is a different orphan class
    // and must not ship. maybeSingle() returns null for 0 rows instead of
    // throwing, so an empty flow → clean fallback, not a swallowed exception.
    if (row.project_type_id && (!row.flow_id || !row.stage_id)) {
      const { data: pt } = await supabase
        .from('project_types')
        .select('default_flow_id')
        .eq('id', row.project_type_id)
        .maybeSingle();
      const defaultFlowId = pt?.default_flow_id;
      if (defaultFlowId) {
        let resolvedStageId = row.stage_id || null;
        if (!resolvedStageId) {
          const { data: firstStep } = await supabase
            .from('stage_flow_steps')
            .select('stage_id')
            .eq('flow_id', defaultFlowId)
            .order('step_order', { ascending: true })
            .limit(1)
            .maybeSingle();
          resolvedStageId = firstStep?.stage_id || null;
        }
        // Commit both or neither — never leave a half-populated project.
        if (resolvedStageId) {
          if (!row.flow_id) row.flow_id = defaultFlowId;
          row.stage_id = resolvedStageId;
        }
      }
    }

    // Race the insert against a hard timeout. Without this, a slow network
    // or stalled Supabase edge node leaves the modal open forever (Bug 1).
    const insertPromise = supabase
      .from('projects')
      .insert(row)
      .select(SELECT_PROJECT)
      .single();
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`Project create timed out after ${CREATE_PROJECT_TIMEOUT_MS}ms`)), CREATE_PROJECT_TIMEOUT_MS)
    );
    const { data, error } = await Promise.race([insertPromise, timeoutPromise]);
    if (error) {
      const code = /row-level security|rls/i.test(error.message) ? 'RLS_DENIED' : 'INSERT_ERROR';
      return { success: false, error: error.message, code };
    }
    return { success: true, record: data };
  } catch (err) {
    const code = /timed out/i.test(err.message) ? 'TIMEOUT' : 'EXCEPTION';
    return { success: false, error: err.message, code };
  }
}

export async function moveProjectStage(recordId, stage) {
  try {
    let stageId = stage;
    if (stage && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(stage)) {
      // FIX 2026-04-08 PM: the kanban passes label_en (e.g. "Design") because
      // flowMap.steps is built from stageLookup which prefers label_en. The
      // old query only checked stage_name (often Spanish), so every drop
      // silently failed with "Stage not found". Match either column.
      const { data } = await supabase
        .from('stages')
        .select('id')
        .or(`label_en.eq.${stage},stage_name.eq.${stage}`)
        .limit(1)
        .maybeSingle();
      if (!data) return { success: false, error: `Stage not found: ${stage}` };
      stageId = data.id;
    }

    const { data: oldProject } = await supabase
      .from('projects')
      .select('project_name, stages!stage_id(stage_name)')
      .eq('id', recordId)
      .single();
    const oldStageName = oldProject?.stages?.stage_name || 'Unknown';

    const { data, error } = await supabase
      .from('projects')
      .update({ stage_id: stageId, updated_at: new Date().toISOString() })
      .eq('id', recordId)
      .select(SELECT_PROJECT)
      .single();
    if (error) return { success: false, error: error.message };

    const newStageName = data?.stages?.stage_name || stage;
    const projectName = oldProject?.project_name || data.project_name || '';
    indexKnowledge(`[Stage Change] ${projectName} moved to ${newStageName} (from ${oldStageName})`, projectName || 'Projects');

    return { success: true, record: data };
  } catch (err) { return { success: false, error: err.message }; }
}

export async function updateProject(recordId, updates) {
  try {
    const row = {};
    if (updates.project_name !== undefined) row.project_name = updates.project_name;
    if (updates.short_description !== undefined) row.short_description = updates.short_description;
    if (updates.owner_id !== undefined) row.owner_id = updates.owner_id || null;
    if (updates.project_type_id !== undefined) row.project_type_id = updates.project_type_id || null;
    if (updates.stage_id !== undefined) row.stage_id = updates.stage_id || null;
    if (updates.vessel_id !== undefined) row.vessel_id = updates.vessel_id || null;
    if (updates.flow_id !== undefined) row.flow_id = updates.flow_id || null;
    if (updates.start_date !== undefined) row.start_date = updates.start_date || null;
    if (updates.end_date !== undefined) row.end_date = updates.end_date || null;
    if (updates.total_value_eur !== undefined) row.total_value_eur = updates.total_value_eur;
    // TICKET-211: emails_seen_at — UI marks project emails as read on open
    if (updates.emails_seen_at !== undefined) row.emails_seen_at = updates.emails_seen_at;

    if (Object.keys(row).length === 0) return { success: true, noop: true };

    // When project_type changes, auto-resolve flow_id + stage_id to the new
    // type's default flow and first step. Prevents orphaned projects sitting
    // in a stage that doesn't belong to their new flow.
    if (row.project_type_id && !updates.stage_id) {
      const { data: pt } = await supabase
        .from('project_types')
        .select('default_flow_id')
        .eq('id', row.project_type_id)
        .maybeSingle();
      if (pt?.default_flow_id) {
        const { data: firstStep } = await supabase
          .from('stage_flow_steps')
          .select('stage_id')
          .eq('flow_id', pt.default_flow_id)
          .order('step_order', { ascending: true })
          .limit(1)
          .maybeSingle();
        if (firstStep?.stage_id) {
          row.flow_id = pt.default_flow_id;
          row.stage_id = firstStep.stage_id;
        }
      }
    }

    row.updated_at = new Date().toISOString();
    const { data, error } = await supabase
      .from('projects')
      .update(row)
      .eq('id', recordId)
      .select(SELECT_PROJECT)
      .single();
    if (error) return { success: false, error: error.message };
    return { success: true, record: data };
  } catch (err) { return { success: false, error: err.message }; }
}

/**
 * TICKET-211 — Mark a project's emails as seen (clears the unread badge).
 * Dedicated helper so views don't need to import the general updateProject()
 * (which is gated by the wiring audit as a mutation-from-view violation).
 * Best-effort: a DB failure just means the badge reappears on next data refresh.
 * @param {string} projectId
 * @returns {Promise<void>}
 */
export async function markProjectEmailsSeen(projectId) {
  try {
    await supabase
      .from('projects')
      .update({ emails_seen_at: new Date().toISOString() })
      .eq('id', projectId);
  } catch {
    // spine-emit-ok: badge clear is UI-only best-effort; reverting on next fetch is acceptable
  }
}

// TICKET-1075: a won deal just became a project -- ask the server to congratulate
// the PMO(s) by push. Best-effort: a failed ping must never block the promotion.
export async function notifyProjectCreated(projectId) {
  try {
    return await apiFetch('/api/data?action=push.project_created', {
      method: 'POST', body: JSON.stringify({ project_id: projectId }),
    });
  } catch (e) {
    logEvent('pmo_new_project_push_failed', e?.message || 'unknown', { projectId });
    return null;
  }
}

// SCHEMA_DRIFT[projects.archived_at]: live `projects` table has no archived_at column
// (verified 2026-04-06 via information_schema). Until migration adds it, we fall back
// to the `status` column ('archived' marker). Tracked in docs/SCHEMA_DRIFT_REPORT.md.
export async function archiveProject(projectId) {
  try {
    const { data, error } = await supabase
      .from('projects')
      .update({ status: 'archived', updated_at: new Date().toISOString() })
      .eq('id', projectId)
      .select(SELECT_PROJECT)
      .single();
    if (error) return { success: false, error: error.message };
    return { success: true, record: data };
  } catch (err) { return { success: false, error: err.message }; }
}

export async function restoreProject(projectId) {
  try {
    const { data, error } = await supabase
      .from('projects')
      .update({ status: 'active', updated_at: new Date().toISOString() })
      .eq('id', projectId)
      .select(SELECT_PROJECT)
      .single();
    if (error) return { success: false, error: error.message };
    return { success: true, record: data };
  } catch (err) { return { success: false, error: err.message }; }
}

export async function fetchStages() {
  try {
    const { data, error } = await supabase.from('stages').select('*').order('sort_order');
    if (error) { console.warn('[stages] fetch error:', error.message); return []; }
    return data || [];
  } catch (err) {
    console.warn('[stages] fetchStages failed:', err.message);
    return [];
  }
}

export async function fetchStageFlows() {
  try {
    const { data, error } = await supabase.from('stage_flows').select('*');
    if (error) return [];
    return data || [];
  } catch { return []; }
}

export async function fetchStageFlowSteps() {
  try {
    const { data, error } = await supabase.from('stage_flow_steps').select('*').order('step_order');
    if (error) return [];
    return data || [];
  } catch { return []; }
}

export async function fetchProjectTypes() {
  try {
    const { data, error } = await supabase.from('project_types').select('*');
    if (error) return [];
    return data || [];
  } catch { return []; }
}

// ─── Bundles / POs (still routed through API endpoints) ──────────
export async function fetchBundles(projectId) {
  const params = new URLSearchParams();
  if (projectId) params.set('project_id', projectId);
  else params.set('all', 'true');
  params.set('action', 'bundles.list');
  try {
    const data = await apiFetch(`${API_BASE}/data?${params}`);
    const bundles = data?.bundles || data?.records || [];
    if (bundles.length > 0) return bundles;
  } catch (e) { /* fall through */ }
  // Fallback: direct Supabase read
  try {
    let query = supabase
      .from('project_bundles')
      .select(BUNDLE_WITH_ITEMS) // TICKET-977 S1b: items are rows, not the deprecated jsonb
      .order('created_at', { ascending: false });
    if (projectId) query = query.eq('project_id', projectId);
    const { data: rows, error } = await query;
    if (error) throw error;
    return (rows || []).map(withBundleItems);
  } catch (e) {
    // TICKET-1042: both the API and this fallback failed — an outage must not read as 'no bundles'.
    logEvent('bundles_fetch_failed', e?.message || 'unknown', { projectId: projectId || null });
  }
  return [];
}

export async function createBundle(payload) {
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({ action: 'bundles.create', ...payload }),
  });
}

export async function updateBundle(recordId, updates) {
  return apiFetch(`${API_BASE}/data`, {
    method: 'POST',
    body: JSON.stringify({ action: 'bundles.update', id: recordId, ...updates }),
  });
}

export async function fetchPurchaseOrders(projectId) {
  const data = await apiFetch(`${API_BASE}/data?action=procurement.orders&project=${projectId}`);
  return data?.records || [];
}

export async function fetchPOLines(poId) {
  const data = await apiFetch(`${API_BASE}/data?action=procurement.po_lines&po_id=${poId}`);
  return data?.records || [];
}

export async function fetchChildProjects(parentProjectId) {
  return { records: [] };
}

export async function fetchProjectById(projectId) {
  const { data, error } = await supabase
    .from('projects')
    .select(SELECT_PROJECT)
    .eq('id', projectId)
    .single();
  if (error) return { success: false, error: error.message };
  return { success: true, record: data };
}
